import * as React from 'react'
import { useDropzone } from 'react-dropzone'
import clx from 'classnames'
import { useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import { useQueryCompanyDatabases } from '@/lib/api/company/useCompanyDatabaseHooks'
import {
  initDatabaseBackupUpload,
  completeDatabaseBackupUpload,
  getUploadStatus,
  InitUploadRequest,
} from '@/lib/api/company/database/uploadBackup'
import { useUser } from '@/modules/shared/context/UserContext'
import { invalidateAccessQueries } from '@/modules/shared/query-client'
import { UserRoleModel } from '@/app/models/createCompanyModel'
import {
  handleError,
  ErrorType,
  ParsedError,
  DatabaseUploadErrorCode
} from '@/lib/api/company/database/errorHandler'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

const POLL_INTERVAL_MS = 2000

function translateStep(status: string, step: string | null | undefined, t: (key: string) => string): string | null {
  const sizeMatch = step?.match(/\d+\.?\d*\s*[KMGB]+\s*\/\s*\d+\.?\d*\s*[KMGB]+/)
  const pctMatch = step?.match(/(\d+)%/)
  switch (status) {
    case 'queued': return t('databases.upload-step-queued')
    case 'downloading': return sizeMatch ? `${t('databases.upload-step-downloading')}: ${sizeMatch[0]}` : t('databases.upload-step-downloading-file')
    case 'extracting': return sizeMatch ? `${t('databases.upload-step-extracting')}: ${sizeMatch[0]}` : t('databases.upload-step-extracting-archive')
    case 'restoring': return pctMatch ? `${t('databases.upload-step-restoring')}: ${pctMatch[1]}%` : t('databases.upload-step-restoring-db')
    case 'creating_company': return t('databases.upload-step-creating-company')
    default: return step ?? null
  }
}

type Props = {
  companyId: string
}

export const DatabaseBackupUpload: React.FC<Props> = ({ companyId }) => {
  const { companyId: routerCompanyId } = useParams<{ companyId: string }>()
  const auth = useAuth()
  const navigate = useNavigate()
  const { t } = useLocalize()
  const queryClient = useQueryClient()
  const { accountData, getRolesInCompany, isLoading: isUserLoading } = useUser()
  const [uploading, setUploading] = React.useState(false)
  const [uploadProgress, setUploadProgress] = React.useState(0)
  const [processingStep, setProcessingStep] = React.useState<string | null>(null)
  const [error, setError] = React.useState<ParsedError | null>(null)
  const [success, setSuccess] = React.useState(false)
  const [uploadId, setUploadId] = React.useState<string | null>(null)
  // Once processing completes we hold the newly-created company id and switch into a
  // "finalizing" waiting state, polling its databases until the restore is visible,
  // then redirect to it — mirroring the add-company flow so the user isn't left guessing.
  const [createdCompanyId, setCreatedCompanyId] = React.useState<string | null>(null)
  const pollingRef = React.useRef<ReturnType<typeof setInterval> | null>(null)

  const { data: createdCompanyDatabases } = useQueryCompanyDatabases(createdCompanyId, {
    waitForMigration: true,
  })

  React.useEffect(() => {
    // Redirect only once the restored database has actually been migrated to the current
    // model (isCompatibleWithModel). Redirecting merely on currentVersion !== null lands on
    // the dashboard mid-migration and triggers a NOT_MIGRATED_COMPANY 500.
    if (
      createdCompanyId !== null &&
      createdCompanyDatabases &&
      createdCompanyDatabases.some((db) => db.isCompatibleWithModel === true)
    ) {
      navigate(`/et/company/${createdCompanyId}/dashboard`)
    }
  }, [createdCompanyDatabases, createdCompanyId, navigate])

  const stopPolling = React.useCallback(() => {
    if (pollingRef.current !== null) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  const startPolling = React.useCallback((id: string) => {
    stopPolling()
    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await getUploadStatus(id)
        setUploadProgress(data.progress ?? 0)
        setProcessingStep(translateStep(data.status, data.step, t))

        if (data.status === 'completed') {
          stopPolling()
          setUploadProgress(100)
          // Includes the account/permissions projection — the restore created a
          // company the cached snapshot has never seen, and landing on its
          // dashboard without it renders the no-permission screen.
          await invalidateAccessQueries(queryClient)
          if (data.companyId) {
            // Enter the finalizing/waiting state; the databases poll + effect above
            // will redirect to the new company once its restore is visible.
            setProcessingStep(t('databases.upload-redirecting'))
            setCreatedCompanyId(data.companyId)
          } else {
            // No company id returned — fall back to the plain success banner.
            setProcessingStep(t('databases.upload-step-done'))
            setSuccess(true)
            setUploading(false)
          }
        } else if (data.status === 'failed') {
          stopPolling()
          setError(handleError(data.errorMessage || t('databases.upload-processing-error')))
          setUploading(false)
        }
      } catch {
        // network blip — keep polling
      }
    }, POLL_INTERVAL_MS)
  }, [stopPolling, t])

  React.useEffect(() => () => stopPolling(), [stopPolling])

  // Check if string contains only ASCII characters
  const isAsciiOnly = (str: string): boolean => {
    // Check if all characters are ASCII (0-127)
    return /^[\x00-\x7F]*$/.test(str)
  }

  // Sanitize filename to show what it will become
  const sanitizeFileName = (fileName: string): string => {
    // Remove extension
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
    
    // Replace Polish characters with ASCII equivalents
    let sanitized = nameWithoutExt
      .replace(/ą/g, 'a')
      .replace(/ć/g, 'c')
      .replace(/ę/g, 'e')
      .replace(/ł/g, 'l')
      .replace(/ń/g, 'n')
      .replace(/ó/g, 'o')
      .replace(/ś/g, 's')
      .replace(/ź/g, 'z')
      .replace(/ż/g, 'z')
      .replace(/Ą/g, 'A')
      .replace(/Ć/g, 'C')
      .replace(/Ę/g, 'E')
      .replace(/Ł/g, 'L')
      .replace(/Ń/g, 'N')
      .replace(/Ó/g, 'O')
      .replace(/Ś/g, 'S')
      .replace(/Ź/g, 'Z')
      .replace(/Ż/g, 'Z')
    
    // Remove non-ASCII characters
    sanitized = sanitized.replace(/[^\x00-\x7F]/g, '_')
    
    // Replace spaces and special chars with underscore
    sanitized = sanitized.replace(/[\s.()[\]{}]/g, '_')
    
    // Remove consecutive underscores
    sanitized = sanitized.replace(/_+/g, '_')
    
    // Remove leading/trailing underscores
    sanitized = sanitized.replace(/^_+|_+$/g, '')
    
    // Truncate to 50 characters
    if (sanitized.length > 50) {
      sanitized = sanitized.substring(0, 50).replace(/_+$/, '')
    }
    
    return sanitized
  }

  const onDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return

      const file = acceptedFiles[0]

      // Validate file type
      const validExtensions = ['.zip', '.7z']
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))

      if (!validExtensions.includes(fileExtension)) {
        setError({
          errorCode: DatabaseUploadErrorCode.INVALID_FILE_TYPE,
          type: ErrorType.VALIDATION,
        })
        return
      }

      // Validate file name for ASCII characters
      const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name
      if (!isAsciiOnly(fileNameWithoutExt)) {
        const sanitized = sanitizeFileName(file.name)
        setError({
          errorCode: DatabaseUploadErrorCode.INVALID_FILE_NAME,
          type: ErrorType.VALIDATION,
          params: { '0': sanitized },
        })
        return
      }

      setError(null)
      setUploading(true)
      setUploadProgress(0)

      try {
        // Get user info to get user ID
        if (!accountData) {
          setError({
            errorCode: DatabaseUploadErrorCode.USER_NOT_LOGGED_IN,
            type: ErrorType.AUTH,
          })
          setUploading(false)
          return
        }

        const userId = accountData.id?.toString() || ''
        if (!userId) {
          setError({
            errorCode: DatabaseUploadErrorCode.USER_ID_NOT_FOUND,
            type: ErrorType.AUTH,
          })
          setUploading(false)
          return
        }

        // Get user roles in current company
        const rolesInCompany = getRolesInCompany(companyId)
        const userRoles: UserRoleModel[] = [
          {
            userId: userId,
            roles: rolesInCompany.map((r) => r.id?.toString() || '').filter((id) => id),
          },
        ]

        // 1. Initialize upload and get presigned URL
        const initRequest: InitUploadRequest = {
          fileName: file.name,
          fileSize: file.size,
          companyId: companyId,
          userId: userId,
          userRoles: userRoles,
        }

        const initResponse = await initDatabaseBackupUpload(initRequest)
        const { uploadUrl, uploadId: newUploadId } = initResponse.data
        setUploadId(newUploadId)

        // 2. Upload file directly to presigned S3 URL (no extra headers — signature only covers 'host')
        setUploadProgress(10)
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
        })

        if (!uploadResponse.ok) {
          setError({
            errorCode: DatabaseUploadErrorCode.UPLOAD_FAILED,
            type: ErrorType.ERROR,
          })
          setUploading(false)
          return
        }

        setUploadProgress(20)
        setProcessingStep(t('databases.upload-file-sent'))

        // 3. Notify backend — returns 202 immediately, processing runs in background
        const completeResponse = await completeDatabaseBackupUpload({ uploadId: newUploadId })

        if (completeResponse.status !== 202) {
          setError(handleError(t('databases.upload-unexpected-response')))
          setUploading(false)
          return
        }

        // 4. Poll status endpoint until completed or failed
        setUploadProgress(25)
        startPolling(newUploadId)
      } catch (err) {
        stopPolling()
        const parsedError = handleError(err)
        setError(parsedError)
        setUploading(false)
      }
    },
    [companyId, accountData, getRolesInCompany, t],
  )

  const isDropzoneDisabled = uploading || isUserLoading || !accountData

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/zip': ['.zip'],
      'application/x-7z-compressed': ['.7z'],
    },
    // Bez tego dowolny plik typu octet-stream przeszedłby jako backup.
    validator: (file) =>
      /\.(zip|7z)$/i.test(file.name) ? null : { code: 'file-invalid-type', message: file.name },
    multiple: false,
    disabled: isDropzoneDisabled,
  })

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-100 tw-p-6">
        <h3 className="tw-mb-2 tw-text-lg tw-font-semibold">{t('databases.upload-backup-title')}</h3>
        <p className="tw-mb-4 tw-text-sm tw-text-content-secondary">
          {t('databases.upload-backup-description')}
        </p>

        {error && (
          <div
            className={clx('tw-mb-4 tw-rounded-md tw-border tw-p-3 tw-text-sm tw-flex tw-items-start tw-gap-2', {
              'tw-bg-blue-50 tw-border-blue-200 tw-text-blue-800': error.type === ErrorType.VALIDATION,
              'tw-bg-yellow-50 tw-border-yellow-200 tw-text-yellow-800': error.type === ErrorType.DUPLICATE,
              'tw-bg-red-50 tw-border-red-200 tw-text-red-800':
                error.type === ErrorType.ERROR || error.type === ErrorType.AUTH || error.type === ErrorType.SERVER,
            })}
          >
            {/* Icon */}
            <div className="tw-flex-shrink-0 tw-mt-0.5">
              {error.type === ErrorType.VALIDATION && (
                <svg className="tw-h-5 tw-w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {error.type === ErrorType.DUPLICATE && (
                <svg className="tw-h-5 tw-w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {(error.type === ErrorType.ERROR || error.type === ErrorType.AUTH || error.type === ErrorType.SERVER) && (
                <svg className="tw-h-5 tw-w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            {/* Error message */}
            <div className="tw-flex-1">
              {(() => {
                let translated = t(error.errorCode)
                if (error.params) {
                  // Replace numbered placeholders {0}, {1}, etc. with parameter values
                  const paramValues = Object.values(error.params)
                  paramValues.forEach((value, index) => {
                    translated = translated.replace(`{${index}}`, String(value))
                  })
                }
                return translated
              })()}
            </div>
          </div>
        )}

        {success && (
          <div className="tw-mb-4 tw-rounded-md tw-bg-green-50 tw-border tw-border-green-200 tw-p-3 tw-text-sm tw-text-green-800">
            {t('databases.upload-success-message')}
          </div>
        )}

        <div
          {...getRootProps()}
          className={clx(
            'tw-rounded-lg tw-border-2 tw-border-dashed tw-p-8 tw-text-center tw-transition-colors',
            {
              'tw-cursor-pointer tw-border-primary tw-bg-primary tw-bg-opacity-5': isDragActive,
              'tw-cursor-pointer tw-border-base-300 hover:tw-border-primary': !isDragActive && !isDropzoneDisabled,
              'tw-cursor-not-allowed tw-border-base-300 tw-opacity-50': isDropzoneDisabled,
            },
          )}
        >
          <input {...getInputProps()} />
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
            <svg
              className="tw-h-12 tw-w-12 tw-text-content-secondary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            {uploading ? (
              <>
                <p className="tw-text-sm tw-font-medium">
                  {processingStep ?? t('databases.upload-uploading-file')}
                </p>
                <div className="tw-w-full tw-max-w-xs">
                  <div className="tw-h-2 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-base-300">
                    <div
                      className="tw-h-full tw-bg-primary tw-transition-all tw-duration-500"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="tw-mt-1 tw-text-xs tw-text-content-secondary tw-text-center">
                    {uploadProgress}%
                  </p>
                </div>
              </>
            ) : isUserLoading ? (
              <p className="tw-text-sm tw-text-content-secondary">{t('databases.upload-loading-user-data')}</p>
            ) : !accountData ? (
              <p className="tw-text-sm tw-text-content-secondary">{t('databases.upload-no-user-data')}</p>
            ) : (
              <>
                <p className="tw-text-sm tw-font-medium">
                  {isDragActive
                    ? t('databases.upload-drop-file')
                    : t('databases.upload-click-or-drag')}
                </p>
                <p className="tw-text-xs tw-text-content-secondary">
                  {t('databases.upload-allowed-formats')}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

